import React, { useState, useEffect } from 'react';
import {
    Card,
    Table,
    Button,
    Modal,
    Form,
    Input,
    InputNumber,
    Select,
    DatePicker,
    Row,
    Col,
    Statistic,
    Progress,
    Tag,
    Space,
    message,
    Tooltip,
    Alert
} from 'antd';
import {
    PlusOutlined,
    BankOutlined,
    CalculatorOutlined,
    TrophyOutlined,
    RiseOutlined,
    DownloadOutlined,
    UploadOutlined,
    HistoryOutlined,
    BarChartOutlined
} from '@ant-design/icons';
import { savingsService } from '../../services/savingsService';
import { useAuth } from '../../../auth/contexts/AuthContext';
import { formatCurrency } from '../../../../shared/utils/formatters';
import dayjs from 'dayjs';
import styles from './SavingsManager.module.css';

import SavingsReport from '../SavingsReport';

const { Option } = Select;
const { TextArea } = Input;



export default function SavingsManager() {
    const { currentUser } = useAuth();
    const [savings, setSavings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [withdrawModalVisible, setWithdrawModalVisible] = useState(false);
    const [depositModalVisible, setDepositModalVisible] = useState(false);
    const [transactionHistoryVisible, setTransactionHistoryVisible] = useState(false);
    const [selectedSaving, setSelectedSaving] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [form] = Form.useForm();
    const [withdrawForm] = Form.useForm();
    const [depositForm] = Form.useForm();
    const [reportVisible, setReportVisible] = useState(false);

    useEffect(() => {
        if (currentUser) {
            fetchSavings();
        }
    }, [currentUser]);

    const fetchSavings = async () => {
        setLoading(true);
        try {
            const savingsList = await savingsService.getSavings(currentUser.uid);
            setSavings(savingsList);
        } catch (error) {
            message.error('Lỗi khi tải danh sách tiết kiệm');
        }
        setLoading(false);
    };

    const handleSubmit = async (values) => {
        try {
            const kyHanThang = parseInt(values.kyHan);
            const ngayDaoHan = dayjs().add(kyHanThang, 'month').toDate();

            const savingsData = {
                tenGoiTietKiem: values.tenGoiTietKiem,
                nganHang: values.nganHang,
                soTienGoc: values.soTienGoc,
                laiSuat: values.laiSuat,
                kyHan: kyHanThang,
                kyHanText: values.kyHanText,
                loaiLai: values.loaiLai,
                ngayDaoHan: ngayDaoHan,
                ghiChu: values.ghiChu || '',
                autoRenewal: values.autoRenewal || false
            };

            await savingsService.addSavings(currentUser.uid, savingsData);
            message.success('Thêm khoản tiết kiệm thành công!');
            setModalVisible(false);
            form.resetFields();
            fetchSavings();
        } catch (error) {
            message.error('Lỗi khi thêm tiết kiệm: ' + error.message);
        }
    };

    const handleWithdraw = async (values) => {
        try {
            await savingsService.withdrawSavings(
                currentUser.uid,
                selectedSaving.id,
                values.withdrawAmount,
                values.reason
            );
            message.success('Rút tiền thành công!');
            setWithdrawModalVisible(false);
            withdrawForm.resetFields();
            setSelectedSaving(null);
            fetchSavings();
        } catch (error) {
            message.error('Lỗi khi rút tiền: ' + error.message);
        }
    };

    const handleDeposit = async (values) => {
        try {
            await savingsService.depositToSavings(
                currentUser.uid,
                selectedSaving.id,
                values.depositAmount
            );
            message.success('Nạp tiền thành công!');
            setDepositModalVisible(false);
            depositForm.resetFields();
            setSelectedSaving(null);
            fetchSavings();
        } catch (error) {
            message.error('Lỗi khi nạp tiền: ' + error.message);
        }
    };

    const showTransactionHistory = async (saving) => {
        try {
            const history = await savingsService.getSavingsTransactions(currentUser.uid, saving.id);
            setTransactions(history);
            setSelectedSaving(saving);
            setTransactionHistoryVisible(true);
        } catch (error) {
            message.error('Lỗi khi tải lịch sử giao dịch');
        }
    };

    const calculateStats = () => {
        const totalPrincipal = savings.reduce((sum, s) => sum + s.soTienGoc, 0);
        const totalCurrent = savings.reduce((sum, s) => {
            const current = savingsService.calculateCurrentValue(s);
            return sum + current.currentValue;
        }, 0);
        const totalInterest = totalCurrent - totalPrincipal;
        const activeSavings = savings.filter(s => s.trangThai === 'active').length;

        return { totalPrincipal, totalCurrent, totalInterest, activeSavings };
    };

    const getStatusTag = (saving) => {
        if (saving.trangThai === 'withdrawn') {
            return <Tag color="default">Đã rút</Tag>;
        }

        const isMatured = dayjs().isAfter(dayjs(saving.ngayDaoHan));
        const daysLeft = dayjs(saving.ngayDaoHan).diff(dayjs(), 'day');

        if (isMatured) {
            return <Tag color="success">Đã đến hạn</Tag>;
        } else if (daysLeft <= 30) {
            return <Tag color="warning">Sắp đến hạn ({daysLeft} ngày)</Tag>;
        } else {
            return <Tag color="processing">Đang gửi ({daysLeft} ngày)</Tag>;
        }
    };

    const stats = calculateStats();

    const columns = [
        {
            title: 'Tên gói tiết kiệm',
            dataIndex: 'tenGoiTietKiem',
            key: 'tenGoiTietKiem',
            render: (text, record) => (
                <div>
                    <div className={styles.savingsName}>{text}</div>
                    <div className={styles.bankName}>
                        <BankOutlined /> {record.nganHang}
                    </div>
                </div>
            )
        },
        {
            title: 'Số tiền gốc',
            dataIndex: 'soTienGoc',
            key: 'soTienGoc',
            render: (amount) => formatCurrency(amount),
            sorter: (a, b) => a.soTienGoc - b.soTienGoc
        },
        {
            title: 'Lãi suất',
            key: 'interest',
            render: (_, record) => (
                <div>
                    <div className={styles.interestRate}>
                        {record.laiSuat}%/năm
                    </div>
                    <div className={styles.compoundType}>
                        {record.loaiLai === 'simple' ? 'Lãi đơn' :
                            record.loaiLai === 'monthly' ? 'Lãi kép (tháng)' :
                                record.loaiLai === 'quarterly' ? 'Lãi kép (quý)' :
                                    record.loaiLai === 'yearly' ? 'Lãi kép (năm)' : 'Lãi kép'}
                    </div>
                </div>
            )
        },
        {
            title: 'Giá trị hiện tại',
            key: 'currentValue',
            render: (_, record) => {
                const current = savingsService.calculateCurrentValue(record);
                const progress = (dayjs().diff(dayjs(record.ngayTao), 'day') /
                    dayjs(record.ngayDaoHan).diff(dayjs(record.ngayTao), 'day')) * 100;

                return (
                    <div>
                        <Statistic
                            value={current.currentValue}
                            formatter={(value) => formatCurrency(value)}
                            valueStyle={{ fontSize: 14, color: '#52c41a' }}
                            prefix={<RiseOutlined />}
                        />
                        <div className={styles.interestEarned}>
                            Lãi: {formatCurrency(current.interestEarned)}
                        </div>
                        <Progress
                            percent={Math.min(100, Math.max(0, progress))}
                            size="small"
                            strokeColor="#52c41a"
                            style={{ marginTop: 4 }}
                        />
                    </div>
                );
            }
        },
        {
            title: 'Trạng thái',
            key: 'status',
            render: (_, record) => getStatusTag(record)
        },
        {
            title: 'Hành động',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Tooltip title="Nạp thêm tiền">
                        <Button
                            icon={<UploadOutlined />}
                            onClick={() => {
                                setSelectedSaving(record);
                                setDepositModalVisible(true);
                            }}
                            size="small"
                            disabled={record.trangThai === 'withdrawn'}
                        />
                    </Tooltip>

                    <Tooltip title="Rút tiền">
                        <Button
                            icon={<DownloadOutlined />}
                            onClick={() => {
                                setSelectedSaving(record);
                                setWithdrawModalVisible(true);
                            }}
                            size="small"
                            disabled={record.trangThai === 'withdrawn'}
                        />
                    </Tooltip>

                    <Tooltip title="Lịch sử giao dịch">
                        <Button
                            icon={<HistoryOutlined />}
                            onClick={() => showTransactionHistory(record)}
                            size="small"
                        />
                    </Tooltip>
                </Space>
            )
        }



    ];

    return (
        <div className={styles.savingsManager}>
            {/* Thống kê tổng quan */}
            <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="Tổng tiền gốc"
                            value={stats.totalPrincipal}
                            formatter={(value) => formatCurrency(value)}
                            prefix={<BankOutlined />}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="Giá trị hiện tại"
                            value={stats.totalCurrent}
                            formatter={(value) => formatCurrency(value)}
                            prefix={<TrophyOutlined />}
                            valueStyle={{ color: '#52c41a' }}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="Lãi tích lũy"
                            value={stats.totalInterest}
                            formatter={(value) => formatCurrency(value)}
                            prefix={<RiseOutlined />}
                            valueStyle={{ color: '#fa8c16' }}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="Số khoản đang gửi"
                            value={stats.activeSavings}
                            suffix="khoản"
                            valueStyle={{ color: '#722ed1' }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Danh sách tiết kiệm */}
            <Card
                title="💰 Danh sách tiết kiệm"
                extra={
                    <Space>
                        <Button
                            icon={<BarChartOutlined />}
                            onClick={() => setReportVisible(true)}
                        >
                            Báo cáo
                        </Button>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => setModalVisible(true)}
                        >
                            Thêm tiết kiệm
                        </Button>
                    </Space>
                }
            >
                <Table
                    columns={columns}
                    dataSource={savings}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showTotal: (total, range) =>
                            `${range[0]}-${range[1]} của ${total} khoản tiết kiệm`
                    }}
                />
            </Card>

            {/* Modal thêm tiết kiệm */}
            <Modal
                title="💰 Thêm khoản tiết kiệm"
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={null}
                width={600}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                >
                    <Form.Item
                        name="tenGoiTietKiem"
                        label="Tên gói tiết kiệm"
                        rules={[{ required: true, message: 'Vui lòng nhập tên gói!' }]}
                    >
                        <Input placeholder="VD: Tiết kiệm định kỳ 12 tháng" />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="nganHang"
                                label="Ngân hàng"
                                rules={[{ required: true, message: 'Vui lòng chọn hoặc nhập ngân hàng!' }]}
                            >
                                <Select
                                    placeholder="Chọn ngân hàng hoặc nhập tên khác"
                                    showSearch
                                    allowClear
                                    mode="combobox"
                                    filterOption={(input, option) =>
                                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    }
                                >
                                    <Option value="Vietcombank">Vietcombank</Option>
                                    <Option value="Techcombank">Techcombank</Option>
                                    <Option value="VPBank">VPBank</Option>
                                    <Option value="ACB">ACB</Option>
                                    <Option value="Sacombank">Sacombank</Option>
                                    <Option value="BIDV">BIDV</Option>
                                    <Option value="Agribank">Agribank</Option>
                                    <Option value="MB Bank">MB Bank</Option>
                                    <Option value="TPBank">TPBank</Option>
                                    <Option value="VietinBank">VietinBank</Option>
                                    <Option value="HDBank">HDBank</Option>
                                    <Option value="OCB">OCB</Option>
                                    <Option value="SHB">SHB</Option>
                                    <Option value="Eximbank">Eximbank</Option>
                                    <Option value="SeABank">SeABank</Option>
                                    <Option value="LienVietPostBank">LienVietPostBank</Option>
                                    <Option value="VIB">VIB</Option>
                                    <Option value="MSB">MSB</Option>
                                    <Option value="Nam A Bank">Nam A Bank</Option>
                                    <Option value="PVcomBank">PVcomBank</Option>
                                </Select>
                            </Form.Item>
                        </Col>


                        <Col span={12}>
                            <Form.Item
                                name="soTienGoc"
                                label="Số tiền gốc"
                                rules={[{ required: true, message: 'Vui lòng nhập số tiền!' }]}
                            >
                                <InputNumber
                                    style={{ width: '100%' }}
                                    formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                                    addonAfter="VNĐ"
                                    min={100000}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item
                                name="laiSuat"
                                label="Lãi suất (%/năm)"
                                rules={[{ required: true, message: 'Vui lòng nhập lãi suất!' }]}
                            >
                                <InputNumber
                                    style={{ width: '100%' }}
                                    min={0}
                                    max={50}
                                    step={0.1}
                                    addonAfter="%"
                                />
                            </Form.Item>
                        </Col>

                        <Col span={8}>
                            <Form.Item
                                name="kyHan"
                                label="Kỳ hạn"
                                rules={[{ required: true, message: 'Vui lòng chọn kỳ hạn!' }]}
                            >
                                <Select
                                    placeholder="Chọn kỳ hạn"
                                    onChange={(value, option) => {
                                        form.setFieldsValue({ kyHanText: option.children });
                                    }}
                                >
                                    <Option value="1">1 tháng</Option>
                                    <Option value="3">3 tháng</Option>
                                    <Option value="6">6 tháng</Option>
                                    <Option value="12">12 tháng</Option>
                                    <Option value="24">24 tháng</Option>
                                    <Option value="36">36 tháng</Option>
                                </Select>
                            </Form.Item>
                            <Form.Item name="kyHanText" hidden>
                                <Input />
                            </Form.Item>
                        </Col>

                        <Col span={8}>
                            <Form.Item
                                name="loaiLai"
                                label="Loại lãi"
                                rules={[{ required: true, message: 'Vui lòng chọn loại lãi!' }]}
                            >
                                <Select placeholder="Chọn loại lãi">
                                    <Option value="simple">Lãi đơn</Option>
                                    <Option value="monthly">Lãi kép (tháng)</Option>
                                    <Option value="quarterly">Lãi kép (quý)</Option>
                                    <Option value="yearly">Lãi kép (năm)</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        name="ghiChu"
                        label="Ghi chú"
                    >
                        <TextArea rows={3} placeholder="Ghi chú thêm..." />
                    </Form.Item>

                    <Form.Item style={{ textAlign: 'right' }}>
                        <Space>
                            <Button onClick={() => setModalVisible(false)}>
                                Hủy
                            </Button>
                            <Button type="primary" htmlType="submit">
                                Thêm tiết kiệm
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Modal rút tiền */}
            <Modal
                title="💸 Rút tiền tiết kiệm"
                open={withdrawModalVisible}
                onCancel={() => setWithdrawModalVisible(false)}
                footer={null}
            >
                {selectedSaving && (
                    <div>
                        <Alert
                            message={`Số dư hiện tại: ${formatCurrency(savingsService.calculateCurrentValue(selectedSaving).currentValue)}`}
                            type="info"
                            style={{ marginBottom: 16 }}
                        />

                        <Form
                            form={withdrawForm}
                            layout="vertical"
                            onFinish={handleWithdraw}
                        >
                            <Form.Item
                                name="withdrawAmount"
                                label="Số tiền rút"
                                rules={[{ required: true, message: 'Vui lòng nhập số tiền!' }]}
                            >
                                <InputNumber
                                    style={{ width: '100%' }}
                                    formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                                    addonAfter="VNĐ"
                                    min={1000}
                                    max={savingsService.calculateCurrentValue(selectedSaving).currentValue}
                                />
                            </Form.Item>

                            <Form.Item
                                name="reason"
                                label="Lý do rút tiền"
                            >
                                <TextArea rows={3} placeholder="Lý do rút tiền (tùy chọn)..." />
                            </Form.Item>

                            <Form.Item style={{ textAlign: 'right' }}>
                                <Space>
                                    <Button onClick={() => setWithdrawModalVisible(false)}>
                                        Hủy
                                    </Button>
                                    <Button type="primary" htmlType="submit">
                                        Rút tiền
                                    </Button>
                                </Space>
                            </Form.Item>
                        </Form>
                    </div>
                )}
            </Modal>

            {/* Modal nạp tiền */}
            <Modal
                title="💵 Nạp thêm tiền"
                open={depositModalVisible}
                onCancel={() => setDepositModalVisible(false)}
                footer={null}
            >
                <Form
                    form={depositForm}
                    layout="vertical"
                    onFinish={handleDeposit}
                >
                    <Form.Item
                        name="depositAmount"
                        label="Số tiền nạp"
                        rules={[{ required: true, message: 'Vui lòng nhập số tiền!' }]}
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={value => value.replace(/\$\s?|(,*)/g, '')}
                            addonAfter="VNĐ"
                            min={10000}
                        />
                    </Form.Item>

                    <Form.Item style={{ textAlign: 'right' }}>
                        <Space>
                            <Button onClick={() => setDepositModalVisible(false)}>
                                Hủy
                            </Button>
                            <Button type="primary" htmlType="submit">
                                Nạp tiền
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Modal lịch sử giao dịch */}
            <Modal
                title="📋 Lịch sử giao dịch"
                open={transactionHistoryVisible}
                onCancel={() => setTransactionHistoryVisible(false)}
                footer={null}
                width={600}
            >
                <Table
                    dataSource={transactions}
                    columns={[
                        {
                            title: 'Ngày',
                            dataIndex: 'ngayGiaoDich',
                            render: (date) => dayjs(date).format('DD/MM/YYYY HH:mm')
                        },
                        {
                            title: 'Loại',
                            dataIndex: 'loaiGiaoDich',
                            render: (type) => (
                                <Tag color={type === 'deposit' ? 'green' : 'red'}>
                                    {type === 'deposit' ? 'Nạp tiền' : 'Rút tiền'}
                                </Tag>
                            )
                        },
                        {
                            title: 'Số tiền',
                            dataIndex: 'soTien',
                            render: (amount) => formatCurrency(amount)
                        },
                        {
                            title: 'Số dư sau GD',
                            dataIndex: 'soDuSauGiaoDich',
                            render: (balance) => formatCurrency(balance)
                        }
                    ]}
                    pagination={false}
                    size="small"
                    rowKey="id"
                />
            </Modal>
            <Modal
                title="📊 Báo cáo thống kê tiết kiệm"
                open={reportVisible}
                onCancel={() => setReportVisible(false)}
                footer={null}
                width="90%"
                style={{ top: 20 }}
            >
                <SavingsReport />
            </Modal>
        </div>
    );
}
